import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, MenuItem, CircularProgress } from "@mui/material";
import ScaleIcon from "@mui/icons-material/Scale";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import KpiCard from "./KpiCard";
import ExportButtons from "./ExportButtons";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import RelatorioDistribuidorService from "../../../service/relatorio_distribuidor_service";

function ReportBalanco() {
	const [search, setSearch] = useState("");
	const [produtoFilter, setProdutoFilter] = useState("todos");
	const [fornecedorFilter, setFornecedorFilter] = useState("todos");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		loadData();
	}, [dateFrom, dateTo, produtoFilter, fornecedorFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const service = new RelatorioDistribuidorService();
			const result = await service.fetchBalanco(dateFrom, dateTo, produtoFilter, fornecedorFilter, search);
			setData(result);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const filtered = data?.balanco ?? [];
	const produtos = data?.produtos ?? [];
	const fornecedores = data?.fornecedores ?? [];

	const totalEntrada = filtered.reduce((s, b) => s + b.entradaKg, 0);
	const totalSaida = filtered.reduce((s, b) => s + b.saidaKg, 0);
	const totalPerda = filtered.reduce((s, b) => s + b.perdaKg, 0);
	const totalEstoque = filtered.reduce((s, b) => s + b.estoqueAtualKg, 0);
	const perdaMedia = totalEntrada > 0 ? (totalPerda / totalEntrada) * 100 : 0;

	const chartData = filtered.map((b) => ({
		produto: b.produto.length > 10 ? b.produto.substring(0, 10) + "..." : b.produto,
		"Entrada (kg)": b.entradaKg,
		"Saída (kg)": b.saidaKg,
		"Perda (kg)": b.perdaKg,
	}));

	const getExportConfig = () => ({
		title: "Relatório de Balanço de Massa",
		subtitle: [
			dateFrom || dateTo ? `Período: ${dateFrom || "-"} a ${dateTo || "-"}` : "",
			produtoFilter !== "todos" ? `Produto: ${produtoFilter}` : "",
			fornecedorFilter !== "todos" ? `Fornecedor: ${fornecedorFilter}` : "",
		].filter(Boolean).join(" | ") || undefined,
		filename: "balanco-de-massa",
		kpis: [
			{ label: "Total Entrada", value: `${totalEntrada.toLocaleString("pt-BR")} kg` },
			{ label: "Total Saída", value: `${totalSaida.toLocaleString("pt-BR")} kg` },
			{ label: "Perda Total", value: `${totalPerda.toLocaleString("pt-BR")} kg (${perdaMedia.toFixed(1)}%)` },
			{ label: "Estoque Atual", value: `${totalEstoque.toLocaleString("pt-BR")} kg` },
		],
		headers: ["Produto", "Fornecedor", "Período", "Entrada (kg)", "Saída (kg)", "Perda (kg)", "Perda (%)", "Estoque (kg)"],
		rows: [
			...filtered.map((b) => [b.produto, b.fornecedor, b.periodo.split("-").reverse().join("/"), b.entradaKg, b.saidaKg, b.perdaKg, `${b.perdaPercent.toFixed(1)}%`, b.estoqueAtualKg]),
			["TOTAL", "", "", totalEntrada, totalSaida, totalPerda, `${perdaMedia.toFixed(1)}%`, totalEstoque],
		],
	});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<ScaleIcon />} title="Total Entrada" value={`${totalEntrada.toLocaleString("pt-BR")} kg`} subtitle="peso bruto recebido" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<InventoryIcon />} title="Total Saída" value={`${totalSaida.toLocaleString("pt-BR")} kg`} subtitle="peso expedido" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<TrendingDownIcon />} title="Perda Total" value={`${totalPerda.toLocaleString("pt-BR")} kg`} trend="down" trendValue={`${perdaMedia.toFixed(1)}% do total`} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<WarningAmberIcon />} title="Estoque Atual" value={`${totalEstoque.toLocaleString("pt-BR")} kg`} subtitle="em unidade distribuidora" />
				</Grid>
			</Grid>

			{/* Filtros */}
			<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
				<CardContent sx={{ py: 1.5, px: 2 }}>
					<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
						<FilterListIcon sx={{ fontSize: 18, color: "#8e9bae" }} />
						<TextField
							size="small"
							placeholder="Buscar produto ou fornecedor..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#8e9bae" }} /></InputAdornment> }}
							sx={{ minWidth: 200, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }}
						/>
						<TextField
							select
							size="small"
							value={produtoFilter}
							onChange={(e) => setProdutoFilter(e.target.value)}
							sx={{ width: 160, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }}
						>
							<MenuItem value="todos">Todos Produtos</MenuItem>
							{produtos.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
						</TextField>
						<TextField
							select
							size="small"
							value={fornecedorFilter}
							onChange={(e) => setFornecedorFilter(e.target.value)}
							sx={{ width: 180, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }}
						>
							<MenuItem value="todos">Todos Fornecedores</MenuItem>
							{fornecedores.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
						</TextField>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<TextField type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 145, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }} />
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>até</Typography>
							<TextField type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ width: 145, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }} />
						</Box>
					</Box>
				</CardContent>
			</Card>

			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4, gap: 1, color: "#8e9bae" }}>
					<CircularProgress size={18} /> <Typography variant="body2">Carregando...</Typography>
				</Box>
			) : error ? (
				<Box sx={{ py: 4, textAlign: "center", color: "#dc2626" }}><Typography variant="body2">Erro ao carregar dados</Typography></Box>
			) : (
				<>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Balanço de Massa por Produto
							</Typography>
							<Box sx={{ height: 290 }}>
								{chartData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
											<CartesianGrid strokeDasharray="3 3" stroke="#dfe3e8" />
											<XAxis dataKey="produto" tick={{ fontSize: 11 }} />
											<YAxis tick={{ fontSize: 11 }} />
											<Tooltip contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
											<Legend wrapperStyle={{ fontSize: 12 }} />
											<Bar dataKey="Entrada (kg)" fill="#1a9e8f" radius={[3, 3, 0, 0]} />
											<Bar dataKey="Saída (kg)" fill="#3498db" radius={[3, 3, 0, 0]} />
											<Bar dataKey="Perda (kg)" fill="#dc2626" radius={[3, 3, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8e9bae" }}>
										<Typography variant="body2">Nenhum dado disponível</Typography>
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>

					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent sx={{ p: 0 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", fontSize: "0.9rem", px: 2.5, pt: 2, pb: 1.5 }}>
								Detalhamento por Produto
							</Typography>
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Produto</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Fornecedor</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Data</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Entrada (kg)</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Saída (kg)</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Perda (kg)</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Perda (%)</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Estoque (kg)</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filtered.length === 0 ? (
											<TableRow>
												<TableCell colSpan={8} sx={{ textAlign: "center", color: "#8e9bae", py: 4 }}>Nenhum registro encontrado</TableCell>
											</TableRow>
										) : (
											<>
												{filtered.map((b, i) => (
													<TableRow key={`${b.produto}-${b.fornecedor}-${b.periodo}-${i}`}>
														<TableCell sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#0f4557" }}>{b.produto}</TableCell>
														<TableCell sx={{ fontSize: "0.8rem", color: "#8e9bae" }}>{b.fornecedor}</TableCell>
														<TableCell sx={{ fontSize: "0.8rem", color: "#8e9bae" }}>{b.periodo.split("-").reverse().join("/")}</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem" }}>{b.entradaKg.toLocaleString("pt-BR")}</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem" }}>{b.saidaKg.toLocaleString("pt-BR")}</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem", color: "#dc2626" }}>{b.perdaKg.toLocaleString("pt-BR")}</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem", color: b.perdaPercent > 3 ? "#dc2626" : "#8e9bae", fontWeight: b.perdaPercent > 3 ? 600 : 400 }}>
															{b.perdaPercent.toFixed(1)}%
														</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{b.estoqueAtualKg.toLocaleString("pt-BR")}</TableCell>
													</TableRow>
												))}
												<TableRow sx={{ bgcolor: "#f5f7f5" }}>
													<TableCell sx={{ fontSize: "0.8rem", fontWeight: 700 }} colSpan={3}>TOTAL</TableCell>
													<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{totalEntrada.toLocaleString("pt-BR")}</TableCell>
													<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{totalSaida.toLocaleString("pt-BR")}</TableCell>
													<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#dc2626" }}>{totalPerda.toLocaleString("pt-BR")}</TableCell>
													<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{perdaMedia.toFixed(1)}%</TableCell>
													<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{totalEstoque.toLocaleString("pt-BR")}</TableCell>
												</TableRow>
											</>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</CardContent>
					</Card>
				</>
			)}
		</Box>
	);
}

export default ReportBalanco;
